import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/stores/auth-store";
import { authAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { navigateAfterAuth } from "@/utils/navigation";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuth();
  const { toast } = useToast();

  const from = location.state?.from?.pathname || "/dashboard";

  // Check for Google OAuth tokens when component mounts (for new tab fallback)
  useEffect(() => {
    const tokens = localStorage.getItem("google-oauth-tokens");
    if (tokens) {
      const { accessToken, refreshToken, expiresAt, user } = JSON.parse(tokens);
      localStorage.removeItem("google-oauth-tokens");
      setAuth(user, accessToken, refreshToken, expiresAt);

      toast({
        title: "Welcome!",
        description: "You have been successfully logged in with Google.",
      });

      navigate(from, { replace: true });
    }

    // Check for errors
    const error = localStorage.getItem("google-oauth-error");
    if (error) {
      localStorage.removeItem("google-oauth-error");
      toast({
        title: "Google login failed",
        description: error,
        variant: "destructive",
      });
    }
  }, [setAuth, toast, navigate, from]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const loginMutation = useMutation({
    mutationFn: authAPI.login,
    onSuccess: (response) => {
      if (response.success && response.data) {
        const { accessToken, refreshToken, expiresAt, user } =
          response.data as any;
        setAuth(user, accessToken, refreshToken, expiresAt);

        toast({
          title: "Welcome back!",
          description: "You have been successfully logged in.",
        });

        navigateAfterAuth(navigate, from);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Login failed",
        description: error.response?.data?.error || "Invalid email or password",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: LoginFormData) => {
    // loginMutation.mutate(data);
  };

  const handleGoogleLogin = async () => {
    navigate("/dashboard");
    try {
      const apiUrl =
        (import.meta as any).env?.VITE_API_URL || "http://localhost:3001/api";

      // Try to open popup
      const popup = window.open(
        `${apiUrl}/auth/google/url`,
        "google-oauth",
        "width=500,height=600,scrollbars=yes,resizable=yes,status=yes,toolbar=no,menubar=no,location=no"
      );

      // Check if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        toast({
          title: "Popup blocked",
          description:
            "Please allow popups for this site and try again, or click the button below to open in a new tab.",
          variant: "destructive",
        });

        // Fallback: open in new tab
        window.open(`${apiUrl}/auth/google/url`, "_blank");
        return;
      }

      // Focus the popup
      popup.focus();

      // Listen for the callback
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);

          // Check for errors first
          const error = localStorage.getItem("google-oauth-error");
          if (error) {
            localStorage.removeItem("google-oauth-error");
            toast({
              title: "Google login failed",
              description: error,
              variant: "destructive",
            });
            return;
          }

          // Check if we have tokens in localStorage (set by callback page)
          const tokens = localStorage.getItem("google-oauth-tokens");
          if (tokens) {
            const { accessToken, refreshToken, expiresAt, user } =
              JSON.parse(tokens);
            localStorage.removeItem("google-oauth-tokens");
            setAuth(user, accessToken, refreshToken, expiresAt);

            toast({
              title: "Welcome!",
              description: "You have been successfully logged in with Google.",
            });

            navigateAfterAuth(navigate, from);
          }
        }
      }, 1000);

      // Also check for tokens periodically in case popup doesn't close properly
      const checkTokens = setInterval(() => {
        const tokens = localStorage.getItem("google-oauth-tokens");
        if (tokens) {
          clearInterval(checkTokens);
          clearInterval(checkClosed);

          const { accessToken, refreshToken, expiresAt, user } =
            JSON.parse(tokens);
          localStorage.removeItem("google-oauth-tokens");
          setAuth(user, accessToken, refreshToken, expiresAt);

          toast({
            title: "Welcome!",
            description: "You have been successfully logged in with Google.",
          });

          navigateAfterAuth(navigate, from);

          // Close popup if it's still open
          if (popup && !popup.closed) {
            popup.close();
          }
        }
      }, 2000);
    } catch (error: any) {
      toast({
        title: "Google login failed",
        description:
          error.response?.data?.error || "Failed to initiate Google login",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">Welcome back</CardTitle>
          <CardDescription className="text-center">
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register("email")}
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  {...register("password")}
                  className={errors.password ? "border-destructive" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </Button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox id="remember" />
                <label htmlFor="remember" className="text-sm">
                  Remember me
                </label>
              </div>
              <Link
                to="/auth/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={loginMutation.isPending}
              disabled={loginMutation.isPending}
            >
              Sign in
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              variant="outline"
              className="mt-4 w-full"
              onClick={handleGoogleLogin}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="text-center text-sm">
        Don't have an account?{" "}
        <Link to="/auth/signup" className="text-primary hover:underline">
          Sign up
        </Link>
      </div>
    </div>
  );
}
